import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import Head from 'next/head';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useT } from '@transifex/react';
import ArrowBack from '@raisin/components-lib/Icons/ArrowBack';
import {
  distributionChannels,
  getDistributionChannel,
  getResizedImageUrl,
  useFeatureFlag,
} from '@raisin/cp-utils';
import { DBSProduct } from 'types';
import { BankDetailsType } from 'types/bank';
import {
  formatCurrencyPosition,
  getBankPageLink,
  getBankPageProductTitle,
  getDepositGuaranteePayoutCurrency,
} from 'utils/bankPageUtils';
import { formatBankAdditionalDetails } from '../../../../utils/bankPageUtils/bankAdditionalDetails';
import BankProductChip from '../../BankProductChips';
import CountryFlag from '../../CountryFlag';
import bankMessages from '../../messages';
import messages from '../messages';
import { BankDetailsProps } from '../types';
import additionalInfoMessages from './../BankAdditionalDetails/messages';
import OffersSection from './/OffersSection';
import BankInfo from './BankInfo';
import * as S from './styles';

const getPromotedProduct = (offers: DBSProduct[], bank: BankDetailsType) =>
  offers.find((offer) => offer.id === bank.deposit_products?.promoted_products[0].id);

const BankDetails: React.FC<BankDetailsProps> = ({
  bank,
  offers,
  bankOfferId,
  highestInterestRate,
  canonicalURL,
}) => {
  const t = useT();
  const intl = useIntl();
  const { variation } = useFeatureFlag();
  const { formatNumber, locale } = intl;
  const distributionChannel = getDistributionChannel(locale);
  const selectedOfferId = bankOfferId;
  const shouldUseBankSlugFallback = variation?.('b2CShouldUseBankSlugFallback', false);
  const bankAdditionalDetails = bank?.additional_details
    ? formatBankAdditionalDetails(bank.additional_details, intl, t, additionalInfoMessages)
    : undefined;

  const isTaxSectionAvailable =
    distributionChannel !== distributionChannels['en-GB'] &&
    bank?.branch_country?.source_tax_description;

  const promotedProduct = useMemo(
    () =>
      bank.deposit_products?.promoted_products && bank.deposit_products.promoted_products.length > 0
        ? (getPromotedProduct(offers, bank) as DBSProduct)
        : null,
    [offers, bank],
  );

  const getBankMetaTitle = (bankName: string, highestRate: string) =>
    t(messages.bankMetaTitle, {
      bank_name: bankName?.trim(),
      highest_rate: `${formatNumber((Number(highestRate) || 0) / 100, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: 'percent',
      })}`,
    });

  const getBankMetaDescription = (bankName: string, highestRate: string) =>
    t(messages.bankMetaDescription, {
      bank_name: bankName,
      highest_rate: `${formatNumber(Number(highestRate) || 0, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`,
    });

  const selectedOffer = offers.find((offer) => offer.id === selectedOfferId);

  const metaTitle = selectedOffer
    ? getBankPageProductTitle(bank, selectedOffer, intl, t)
    : getBankMetaTitle(bank.name, highestInterestRate);

  const metaDescription = getBankMetaDescription(bank.name, highestInterestRate);

  const metaRobots = selectedOffer ? 'noindex, follow' : 'index, follow';

  const currency = bank?.conditions?.currency || undefined;

  const bankDepositGuaranteeCurrency = useMemo(
    () =>
      getDepositGuaranteePayoutCurrency(
        bank?.statutory_deposit_guarantee_scheme?.country?.id,
        distributionChannel,
      ) || t(messages.bankListItemNoCurrency),
    [bank, locale],
  );

  const minInvestmentAmount = formatCurrencyPosition(
    formatNumber(
      bank.conditions.investment_amount_min ? +bank.conditions.investment_amount_min : 0,
      {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      },
    ),
    locale,
  );

  const maxInvestmentAmount = formatCurrencyPosition(
    formatNumber(
      bank.conditions.investment_amount_max ? +bank.conditions.investment_amount_max : 0,
      {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      },
    ),
    locale,
  );

  const securityProtection = t(bankMessages.banksProtectionLimitValue, {
    amount: formatCurrencyPosition(
      formatNumber(parseInt(bank.statutory_deposit_guarantee_scheme.insured_amount), {
        style: 'currency',
        currency: bank.statutory_deposit_guarantee_scheme.currency,
        maximumFractionDigits: 0,
      }),
      locale,
    ),
    currency: bankDepositGuaranteeCurrency,
  });

  const depositMinMax = currency
    ? t(bankMessages.banksDepositAmountValue, {
        minAmount: minInvestmentAmount,
        maxAmount: maxInvestmentAmount,
      })
    : t(messages.bankListItemNoCurrency);

  const bankInfo = [
    {
      value: (
        <div>
          <span>{bank.statutory_deposit_guarantee_scheme.country.name}</span>
          {bank.branch_country?.id && (
            <CountryFlag
              countryId={bank.statutory_deposit_guarantee_scheme.country.id}
              altText={bank.statutory_deposit_guarantee_scheme.country.name}
            />
          )}
          {bank.statutory_deposit_guarantee_scheme.country.rating && (
            <span>{`(${bank.statutory_deposit_guarantee_scheme.country.rating})`}</span>
          )}
        </div>
      ),
    },
    {
      value: (
        <S.DepositInfoContainer>
          <S.DepositInfo>
            {offers.length > 0 ? (
              <>
                <S.DepositLabel>{t(bankMessages.banksDepositAmountTitle)}</S.DepositLabel>{' '}
                {depositMinMax}
              </>
            ) : (
              <S.DepositLabel>{t(messages.bankListItemNoActiveProducts)}</S.DepositLabel>
            )}
          </S.DepositInfo>
          <S.DepositInfo>
            <S.DepositLabel>{t(messages.banksProtectionLimitTitle)}</S.DepositLabel>{' '}
            {securityProtection}
          </S.DepositInfo>
        </S.DepositInfoContainer>
      ),
    },
    {
      value: <BankProductChip bank={bank} distributionChannel={distributionChannel} />,
    },
  ];

  return (
    <S.BankDetailsContainer>
      <Head>
        <meta name="robots" content={metaRobots} />
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalURL} />
      </Head>
      <Box my={7}>
        <S.BackButton
          href={getBankPageLink(locale, shouldUseBankSlugFallback, '', '')}
          data-testid="back-button"
          mode="primaryText"
          hidden={!!selectedOfferId}
          startIcon={<ArrowBack />}
        >
          <span>{t(messages.backButton)}</span>
        </S.BackButton>
      </Box>
      <S.BankInfoSection container component="section">
        <Grid item xs={12} md={8}>
          <S.BankNameTitle variant="h3">{bank.name}</S.BankNameTitle>
          <BankInfo data={bankInfo} />
        </Grid>
        <Grid item md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
          <S.LogoContainer>
            <img
              src={getResizedImageUrl({
                resizableUrl: bank?.logo_url,
                fallbackUrl: bank?.bank_logo_url,
                params: { width: 320 },
              })}
              alt={`logo of ${bank.name}`}
            />
          </S.LogoContainer>
        </Grid>
      </S.BankInfoSection>
      <OffersSection
        offers={offers}
        bank={bank}
        selectedOfferId={selectedOfferId as string}
        promotedProduct={promotedProduct}
        isTaxSectionAvailable={isTaxSectionAvailable}
        bankAdditionalDetails={bankAdditionalDetails}
      />
    </S.BankDetailsContainer>
  );
};

export default BankDetails;
